import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarOutlined,
  CheckCircleFilled,
  CheckCircleOutlined,
  ExceptionOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Alert, Button, Checkbox, Modal, Popconfirm, Row, Steps, message } from "antd";
import { connect } from "dva";
import { router } from "umi";
import { useScreenDetector } from "@/utils/dom";
import { CompanyStatus } from "@/utils/enum";
import {
  childShipmentExchangeIsValid,
  childShipmentExchangeOfferIsValid,
  parentShipmentExchangeIsValid,
  parentShipmentExchangeOfferIsValid,
} from "@/utils/exchangeShipment";
import { hideJiraWidget, showJiraWidget } from "@/utils/utils";
import { getDrivers } from "@/services/associatedUsers";
import { getTransportersAllowed } from "@/services/transporter";
import { getApiCaller } from "./utils/api";
import CompanyAndUserInputForm from "./components/CompanyAndUserInputBody/CompanyAndUserInputForm";
import ConfirmForDispatchBody from "./components/ConfirmForDispatchBody";
import EnterETAsInputBody from "./components/EnterETAsInputBody";
import ShipmentRequirementsSummary from "./components/ShipmentRequirementsSummary";

const { Step } = Steps;
const { confirm } = Modal;

const DispatchStep = {
  ReviewRequirements: 0,
  SelectCompanyAndUser: 1,
  EnterETAs: 2,
  ConfirmForDispatch: 3,
};

const nextButtonPopconfirmText = [
  "Please review all requirements before continuing.",
  "Please enter company and driver before continuing.",
  "Please review all requirements and confirm ETAs for all selected shipments before continuing.",
  "Please accept the terms and conditions before continuing.",
];

const DispatchStepsModal = ({ visible, setVisible, selectedShipments, refreshParentTable, configPerm }) => {
  const { isMobile } = useScreenDetector();

  const entityAndUserFormRef = useRef();
  const etasInputFormRef = useRef();
  const alreadyFetchedTransporters = useRef(false);

  /* Tracks the step being viewed currently */
  const [currentStep, setCurrentStep] = useState(DispatchStep.ReviewRequirements);

  /* Tracks the steps that have been completed */
  const [completedSteps, setCompletedSteps] = useState([]);

  /* Should be one-to-one mapping between shipment info from selected rows and shipment info needed for dispatch through this component */
  const [shipmentDispatchList, setShipmentDispatchList] = useState([]);

  /* Tracks if the user has scrolled to the bottom of the requirements summary at least once for the selected shipments list */
  const [hasScrolledToBottomOfRequirements, setHasScrolledToBottomOfRequirements] = useState(false);

  /* Tracks if the user has checked the box to accept terms/conditions before dispatching selected shipments */
  const [termsAndConditionsAccepted, setTermsAndConditionsAccepted] = useState(false);

  /* Tracks if the user has checked the box to verify they have reviewed all requirements for the selected shipments */
  const [hasReviewedRequirements, setHasReviewedRequirements] = useState(false);

  /* Whether to show the blocker text when some part of the current step is incomplete when pressing the "Next" button */
  const [nextButtonPopconfirmVisible, setNextButtonPopconfirmVisible] = useState(false);

  const [selectedTransporterEntityId, setSelectedTransporterEntityId] = useState();
  const [selectedTransporterUserId, setSelectedTransporterUserId] = useState();
  const [transporterUsersList, setTransporterUsersList] = useState([]);
  const [transporterEntitiesList, setTransporterEntitiesList] = useState([]);

  const [loadingDispatchSelected, setLoadingDispatchSelected] = useState(false);

  const [loadingAllTransporterEntities, setLoadingAllTransporterEntities] = useState(false);

  const [loadingTransporterUsers, setLoadingTransporterUsers] = useState(false);

  const resetDispatchProcess = useCallback(() => {
    setCurrentStep(DispatchStep.ReviewRequirements);
    setCompletedSteps([]);
    setShipmentDispatchList([]);

    /*
      State reset for Step 1:
    */
    setHasReviewedRequirements(false);
    setHasScrolledToBottomOfRequirements(false);

    /*
      State adjustments for Step 3:
    */
    // None, state is maintained on the shipmentDispatchList

    /*
      State adjustments for Step 4:
    */
    setTermsAndConditionsAccepted(false);
  }, []);

  /*
    Triggered upon clicking submit button at end of process
  */
  const handleSubmit = useCallback(
    async (shipmentsToDispatch, userId, entityId) => {
      if (shipmentsToDispatch.length === 0) return;

      setLoadingDispatchSelected(true);

      const shipmentItems = shipmentsToDispatch.map(s => {
        // Grab selected dates
        const pickupEta = new Date(s.pickupETADate);
        const dropoffEta = new Date(s.dropoffETADate);
        const exchangePickupEta = s.exchangePickupETADate ? new Date(s.exchangePickupETADate) : null;
        const exchangeDropoffEta = s.exchangeDropoffETADate ? new Date(s.exchangeDropoffETADate) : null;

        const exchangeId = s.childShipmentId ?? s.parentShipmentId;

        return {
          shipmentId: s.id,
          pickupETA: pickupEta.toISOString(),
          dropOffETA: dropoffEta.toISOString(),
          pickupEtaTimeSlot: s.pickupETATimeSlotIndex,
          dropOffEtaTimeSlot: s.dropoffETATimeSlotIndex,
          acceptedChildShipmentId: s.userAcceptedExchangeOffer ? exchangeId : null,
          childPickupETA: exchangePickupEta?.toISOString() ?? null,
          childDropOffETA: exchangeDropoffEta?.toISOString() ?? null,
          childPickupTimeSlot: s.exchangePickupETATimeSlotIndex ?? null,
          childDropOffTimeSlot: s.exchangeDropoffETATimeSlotIndex ?? null,
        };
      });

      const payload = {
        userId,
        transporterId: entityId,
        items: shipmentItems,
      };

      const { caller: dispatchShipment, payload: finalPayload } = getApiCaller(
        configPerm,
        shipmentsToDispatch?.at(0)?.status,
        payload,
      );

      dispatchShipment(finalPayload)
        .then(loadId => {
          message.success("Successfully dispatched shipments");
          setVisible(false);
          resetDispatchProcess();
          refreshParentTable?.();

          // only for transporters accepting a shipment from the shipment detail view
          if (configPerm["CoreService.AvailableLoads.Accept"] && loadId) {
            router.replace(`/loads/progress/view?loadId=${loadId}`);
          }
        })
        .catch(error => {
          message.error("Error dispatching shipments");
          console.error(error);
        })
        .finally(() => {
          setLoadingDispatchSelected(false);
        });
    },
    [configPerm, resetDispatchProcess, refreshParentTable],
  );

  /* Unique combination of shipment ids in the dispatch list. Used to trigger updating the list when selected shipments change */
  const concatenatedShipmentIds = useMemo(() => {
    return selectedShipments
      ?.map(s => s.shipmentId)
      ?.sort()
      ?.join("-");
  }, [selectedShipments]);

  /*
    Manages updating the internal state of the list of shipments being dispatched when the selected list has changed
  */
  useEffect(() => {
    if (!selectedShipments) return;
    if (selectedShipments.length === 0) {
      // Empty selection means rows were cleared, reset internal state of this component
      resetDispatchProcess();
      return;
    }

    // add/remove items to dispatch list accordingly
    reconcileShipmentLists();
    handleShipmentsForDispatchChanged();
  }, [
    concatenatedShipmentIds,
    selectedShipments,
    resetDispatchProcess,
    reconcileShipmentLists,
    handleShipmentsForDispatchChanged,
  ]);

  /*
    Hide/Show Yellow Jira Widget Icon when opening modal.
    Blocks button on mobile otherwise.
  */
  useEffect(() => {
    if (visible) {
      hideJiraWidget();
    } else {
      showJiraWidget();
    }
  }, [visible]);

  /*
    Checks that valid etas are input for all selected shipments, and any exchange offers have been responded to
  */
  const etasValidatedForAllShipments = useMemo(
    () =>
      shipmentDispatchList.every(s => {
        const etasForMainShipmentSet = s.etasValidated;

        const hasPendingExchangeOffer =
          childShipmentExchangeOfferIsValid(s, configPerm) || parentShipmentExchangeOfferIsValid(s, configPerm);

        const hasValidExchangeShipment =
          childShipmentExchangeIsValid(s, configPerm) || parentShipmentExchangeIsValid(s, configPerm);
        const etasForExchangeShipmentSet = hasValidExchangeShipment
          ? s.userAcceptedExchangeOffer === false ||
            (s.userAcceptedExchangeOffer && s.exchangeEtasValidated && !s.userEditingExchangeEtas)
          : true;

        return !hasPendingExchangeOffer && etasForMainShipmentSet && etasForExchangeShipmentSet;
      }),
    [shipmentDispatchList, configPerm],
  );

  const handleShipmentsForDispatchChanged = useCallback(() => {
    /*
      State adjustments for Step 1:
    */
    // Bump user back to first step, since one or more shipments needs requirements reviewed now
    setCurrentStep(DispatchStep.ReviewRequirements);
    setCompletedSteps([]);
    setHasReviewedRequirements(false);
    setHasScrolledToBottomOfRequirements(false);

    // Scroll the requirements list back to the top to ensure the user reviews the shipments again before continuing
    setTimeout(() => {
      const element = document.querySelector(".shipment-requirements-list");
      if (element) element.scrollTop = 0;
    }, 0); // Delay of 0 milliseconds to ensure execution after initial render

    /*
      State adjustments for Step 2:
    */
    // None, transporter entities and transporter users will be reset the next time the modal is opened

    /*
      State adjustments for Step 3:
    */
    // None, state is maintained on the shipmentDispatchList

    /*
      State adjustments for Step 4:
    */
    setTermsAndConditionsAccepted(false);
  }, []);

  /**
   *
   * Compares newly selected shipments list passed to the component against the current internal list.
   * Adds/Removes items from the internal list to match the newly selected shipments list
   *
   */
  const reconcileShipmentLists = useCallback(() => {
    setShipmentDispatchList(currentDispatchList => {
      const selectedShipmentsCopy = [...selectedShipments];
      // Filter down selected shipmentIds to remove any unordered exchanges that were both selected,
      // since the other will be offered during dispatch process
      // TODO: This is not good behavior, but I don't know how to gray out the exchange shipment's checkbox in the list when one is checked, so for now this has to do - JH
      for (let i = 0; i < selectedShipmentsCopy.length; i++) {
        const { childShipmentId, parentShipmentId } = selectedShipmentsCopy[i];
        const exchangeId = childShipmentId || parentShipmentId;
        const exchangeShipmentIndex = selectedShipmentsCopy.findIndex(s => s.id === exchangeId);

        if (exchangeShipmentIndex !== -1) {
          // Found exchange shipment for this shipment in selected list, need to remove exchange shipment from list
          selectedShipmentsCopy.splice(exchangeShipmentIndex, 1);
        }
      }

      const newSelectedShipmentIds = selectedShipmentsCopy.map(s => s.id);
      const currentDispatchShipmentIds = currentDispatchList.map(s => s.id);
      const shipmentIdsSet = new Set();

      currentDispatchShipmentIds.forEach(id => shipmentIdsSet.add(id));
      newSelectedShipmentIds.forEach(id => shipmentIdsSet.add(id));

      const newDispatchList = [...currentDispatchList];

      shipmentIdsSet.forEach(id => {
        const isInSelectedShipments = selectedShipments.some(s => s.id === id);
        const isInCurrentDispatchList = currentDispatchList.some(s => s.id === id);

        if (isInCurrentDispatchList && !isInSelectedShipments) {
          // Remove from dispatch list
          newDispatchList.splice(
            currentDispatchList.findIndex(s => s.id === id),
            1,
          );
        } else if (!isInCurrentDispatchList && isInSelectedShipments) {
          // Add to dispatch list
          newDispatchList.push(selectedShipments.find(s => s.id === id));
        }

        // In this case, it must be in both, so no action necessary
      });

      // Create Location Objects in items in list if not present
      // (admin shipments response different structure from transporter shipments available response)

      return newDispatchList;
    });
  }, [selectedShipments]);

  const handleClickStep = useCallback(
    stepNumberClicked => {
      if (
        completedSteps.includes(stepNumberClicked) ||
        (stepNumberClicked === DispatchStep.ConfirmForDispatch && completedSteps.includes(DispatchStep.EnterETAs))
      ) {
        setCurrentStep(stepNumberClicked);
        return;
      }
    },
    [completedSteps],
  );

  const renderDispatchSteps = useMemo(() => {
    return (
      <Steps type="navigation" size="small" current={currentStep}>
        <Step
          title={
            <>
              <span>Review Requirements</span>
              {completedSteps.includes(DispatchStep.ReviewRequirements) && (
                <CheckCircleFilled
                  style={{
                    color: "#52c41a",
                    marginLeft: 5,
                  }}
                />
              )}
            </>
          }
          icon={<ExceptionOutlined />}
          onClick={() => handleClickStep(DispatchStep.ReviewRequirements)}
          style={{ cursor: "pointer" }}
        />
        <Step
          title={
            <>
              <span>Select Company and Driver</span>
              {completedSteps.includes(DispatchStep.SelectCompanyAndUser) && (
                <CheckCircleFilled
                  style={{
                    color: "#52c41a",
                    marginLeft: 5,
                  }}
                />
              )}
            </>
          }
          icon={<UserOutlined />}
          onClick={() => handleClickStep(DispatchStep.SelectCompanyAndUser)}
          style={{ cursor: "pointer" }}
        />
        <Step
          title={
            <>
              <span>Enter ETAs</span>
              {completedSteps.includes(DispatchStep.EnterETAs) && (
                <CheckCircleFilled
                  style={{
                    color: "#52c41a",
                    marginLeft: 5,
                  }}
                />
              )}
            </>
          }
          icon={<CalendarOutlined />}
          onClick={() => handleClickStep(DispatchStep.EnterETAs)}
          style={{ cursor: "pointer" }}
        />
        <Step
          title="Confirm for Dispatch"
          icon={<CheckCircleOutlined />}
          onClick={() => handleClickStep(DispatchStep.ConfirmForDispatch)}
          style={{ cursor: "pointer" }}
        />
      </Steps>
    );
  }, [currentStep, completedSteps, handleClickStep]);

  const handleClickCancel = useCallback(() => {
    resetDispatchProcess();
    setVisible(false);

    // Only override "has scrolled" status when the user didn't active the confirm checkbox
    setHasScrolledToBottomOfRequirements(hasReviewedRequirements || currentStep > DispatchStep.ReviewRequirements);
  }, [resetDispatchProcess]);

  const handleClickNext = useCallback(async () => {
    if (currentStep === DispatchStep.ReviewRequirements && !hasReviewedRequirements) {
      setNextButtonPopconfirmVisible(true);
      return;
    }

    if (currentStep === DispatchStep.SelectCompanyAndUser) {
      try {
        await entityAndUserFormRef.current.validateFields();
      } catch (err) {
        console.log(err);
        setNextButtonPopconfirmVisible(true);
        return;
      }
    }

    if (currentStep === DispatchStep.EnterETAs) {
      if (!etasValidatedForAllShipments) {
        setNextButtonPopconfirmVisible(true);
        return;
      }
    }

    if (currentStep === DispatchStep.ConfirmForDispatch) {
      if (!termsAndConditionsAccepted) {
        setNextButtonPopconfirmVisible(true);
        return;
      }

      confirm({
        title: "Warning",
        content: `Are you sure you want to dispatch these shipments?`,
        onOk() {
          handleSubmit(shipmentDispatchList, selectedTransporterUserId, selectedTransporterEntityId);
        },
        okText: "Yes",
        cancelText: "Cancel",
      });
      return;
    }

    setCurrentStep(current => {
      const newCurrentStep = current + 1;

      if (!completedSteps.includes(current)) {
        setCompletedSteps(currentCompletedSteps => [...currentCompletedSteps, current]);
      }

      return newCurrentStep;
    });
  }, [
    currentStep,
    completedSteps,
    hasReviewedRequirements,
    etasValidatedForAllShipments,
    termsAndConditionsAccepted,
    shipmentDispatchList,
    selectedTransporterUserId,
    selectedTransporterEntityId,
    handleSubmit,
  ]);

  const renderFooterButtons = useMemo(() => {
    return (
      <div style={{ textAlign: "right" }}>
        <Button onClick={handleClickCancel} style={{ marginRight: 8 }}>
          Cancel
        </Button>
        <Popconfirm
          title={nextButtonPopconfirmText[currentStep]}
          visible={nextButtonPopconfirmVisible}
          onConfirm={() => setNextButtonPopconfirmVisible(false)}
          cancelButtonProps={{
            style: { display: "none" },
          }}
        >
          <Button
            type="primary"
            onClick={handleClickNext}
            loading={loadingDispatchSelected}
            disabled={transporterEntitiesList.length === 0}
          >
            {currentStep === DispatchStep.ConfirmForDispatch ? "Submit" : "Next"}
          </Button>
        </Popconfirm>
      </div>
    );
  }, [
    currentStep,
    nextButtonPopconfirmVisible,
    loadingDispatchSelected,
    transporterEntitiesList.length,
    handleClickCancel,
    handleClickNext,
  ]);

  const handleReviewRequirementsChecked = useCallback(checked => {
    setHasReviewedRequirements(checked);
    setHasScrolledToBottomOfRequirements(hasScrolledToBottomOfRequirements || checked);
  }, []);

  const handleChangeTermsAndConditionsCheckBox = useCallback(event => {
    setTermsAndConditionsAccepted(event.target.checked);
  }, []);

  const renderNoTransporterEntitiesBody = useCallback(() => {
    return (
      <div
        style={{
          display: "flex",
          minHeight: "350px",
          alignItems: "center",
        }}
      >
        <Alert
          message="No Transporter Entities"
          description="There are no Transporter Entities in the system. Please contact an Autosled admin to continue."
          type="info"
          showIcon
          style={{ width: "100%" }}
        />
      </div>
    );
  }, []);

  const renderReviewRequirementsBody = useCallback(() => {
    return (
      <>
        <div
          style={{
            fontWeight: "bold",
            fontSize: 20,
          }}
        >
          Shipments Selected for Dispatch
        </div>
        <ShipmentRequirementsSummary
          shipmentsList={shipmentDispatchList}
          hasCheckedAllVehicles={hasReviewedRequirements}
          setHasCheckedAllVehicles={handleReviewRequirementsChecked}
          setHasScrolledToBottomOfRequirements={setHasScrolledToBottomOfRequirements}
        />
        {!hasReviewedRequirements && !hasScrolledToBottomOfRequirements ? (
          <div style={{ marginTop: "16px" }}>
            <Alert
              type="warning"
              banner
              message="Please scroll to the bottom of the list above, and check the box to continue."
            />
          </div>
        ) : (
          <div style={{ height: "54px" }} />
        )}
      </>
    );
  }, [
    shipmentDispatchList,
    hasReviewedRequirements,
    hasScrolledToBottomOfRequirements,
    handleReviewRequirementsChecked,
  ]);

  const fetchTransporters = useCallback(
    async searchText => {
      const userHasSelfDispatchPermission = !!configPerm?.["CoreService.AvailableLoads.Accept"];

      const isAdmin = !!configPerm?.["CoreService.AdminShipments"];

      // In case of drivers viewing available loads, do not fetch
      if (!userHasSelfDispatchPermission && !isAdmin) return [];

      return getTransportersAllowed({
        page: 1,
        pageSize: 10,
        filter: searchText,
        ...(!isAdmin && { canSelfDispatch: true }),
        status: CompanyStatus.Active,
      })
        .then(response => {
          setTransporterEntitiesList(response?.items || []);
        })
        .catch(err => {
          console.log(err);
          message.error("Error fetching transporters");
        })
        .finally(() => {
          setLoadingAllTransporterEntities(false);
        });
    },
    [configPerm],
  );

  const loadNewTransporterUsersList = useCallback(
    transporterId => {
      if (!transporterId) return;

      setLoadingTransporterUsers(true);
      setSelectedTransporterUserId(null);

      getDrivers({
        filterId: transporterId,
        shipmentIds: shipmentDispatchList?.map(ship => ship.id),
      })
        .then(async response => {
          const selectableList = response?.items ?? [];

          setTransporterUsersList(selectableList);

          const nonDisabledUsers = selectableList.filter(user => user.isElegible);

          const firstUser = nonDisabledUsers[0];
          if (nonDisabledUsers.length === 1) {
            setSelectedTransporterUserId(firstUser.userId);
          }
        })
        .catch(err => {
          console.log(err);
          message.error("Error fetching users under transporter company");
        })
        .finally(() => {
          setLoadingTransporterUsers(false);
        });
    },
    [shipmentDispatchList],
  );

  const renderCompanyAndUserBody = useCallback(() => {
    return (
      <CompanyAndUserInputForm
        shipmentsList={shipmentDispatchList}
        entityAndUserFormRef={entityAndUserFormRef}
        selectedTransporterEntityId={selectedTransporterEntityId}
        setSelectedTransporterEntityId={setSelectedTransporterEntityId}
        selectedTransporterUserId={selectedTransporterUserId}
        setSelectedTransporterUserId={setSelectedTransporterUserId}
        transporterUsersList={transporterUsersList}
        transporterEntitiesList={transporterEntitiesList}
        fetchTransporters={fetchTransporters}
        loadingAllTransporterEntities={loadingAllTransporterEntities}
        loadingTransporterUsers={loadingTransporterUsers}
        loadNewTransporterUsersList={loadNewTransporterUsersList}
        setTransporterUsersList={setTransporterUsersList}
      />
    );
  }, [
    shipmentDispatchList,
    selectedTransporterEntityId,
    selectedTransporterUserId,
    transporterUsersList,
    transporterEntitiesList,
    loadingAllTransporterEntities,
    loadingTransporterUsers,
    setSelectedTransporterEntityId,
    setSelectedTransporterUserId,
    fetchTransporters,
    loadNewTransporterUsersList,
  ]);

  const renderEnterETAsBody = useCallback(() => {
    return (
      <EnterETAsInputBody
        shipmentsList={shipmentDispatchList}
        setShipmentsList={setShipmentDispatchList}
        etasInputFormRef={etasInputFormRef}
        etasValidatedForAllShipments={etasValidatedForAllShipments}
        configPerm={configPerm}
      />
    );
  }, [shipmentDispatchList, etasValidatedForAllShipments, configPerm, setShipmentDispatchList]);

  const renderConfirmForDispatchBody = useCallback(() => {
    return (
      <>
        <ConfirmForDispatchBody
          shipmentsList={shipmentDispatchList}
          selectedTransporterEntityId={selectedTransporterEntityId}
          selectedTransporterUserId={selectedTransporterUserId}
          transporterUsersList={transporterUsersList}
          transporterEntitiesList={transporterEntitiesList}
        />
        <Row justify="end">
          <div
            style={{
              maxWidth: "40%",
              marginTop: 16,
            }}
          >
            <Checkbox checked={termsAndConditionsAccepted} onChange={handleChangeTermsAndConditionsCheckBox}>
              I verify I have read the{" "}
              <a href="https://autosled.com/carrier_terms_of_use/" target="_blank" rel="noopener noreferrer">
                Terms and Conditions
              </a>
              &nbsp; and{" "}
              <a href="https://www.autosled.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
            </Checkbox>
          </div>
        </Row>
      </>
    );
  }, [
    shipmentDispatchList,
    selectedTransporterEntityId,
    selectedTransporterUserId,
    transporterUsersList,
    transporterEntitiesList,
    termsAndConditionsAccepted,
    handleChangeTermsAndConditionsCheckBox,
  ]);

  const renderBodyForCurrentStep = useMemo(() => {
    if (transporterEntitiesList.length === 0 && !alreadyFetchedTransporters.current) {
      return renderNoTransporterEntitiesBody();
    }

    switch (currentStep) {
      case DispatchStep.ReviewRequirements:
        return renderReviewRequirementsBody();
      case DispatchStep.SelectCompanyAndUser:
        return renderCompanyAndUserBody();
      case DispatchStep.EnterETAs:
        return renderEnterETAsBody();
      case DispatchStep.ConfirmForDispatch:
        return renderConfirmForDispatchBody();
      default:
        return null;
    }
  }, [
    currentStep,
    transporterEntitiesList.length,
    renderNoTransporterEntitiesBody,
    renderReviewRequirementsBody,
    renderCompanyAndUserBody,
    renderEnterETAsBody,
    renderConfirmForDispatchBody,
  ]);

  // Refresh selectors form values from state in parent
  useEffect(() => {
    if (!transporterEntitiesList || transporterEntitiesList.length === 0) return;
    entityAndUserFormRef?.current?.setFieldsValue({
      entityId: selectedTransporterEntityId,
      userId: selectedTransporterUserId,
    });
  }, [selectedTransporterEntityId, selectedTransporterUserId, transporterEntitiesList]);

  useEffect(() => {
    // Don't fetch transporters if there are no shipments selected
    if (shipmentDispatchList.length === 0) {
      return;
    }

    if (transporterEntitiesList?.length === 0 && !alreadyFetchedTransporters.current) {
      // Fetch transporters
      fetchTransporters("");
      alreadyFetchedTransporters.current = true;
    } else if (transporterEntitiesList?.length === 1 && !selectedTransporterEntityId) {
      // Already fetched, select the only transporter entity
      setSelectedTransporterEntityId(transporterEntitiesList[0].id);
      loadNewTransporterUsersList(transporterEntitiesList[0].id);
    }
  }, [transporterEntitiesList, fetchTransporters, loadNewTransporterUsersList, shipmentDispatchList]);

  return (
    <Modal
      title={renderDispatchSteps}
      visible={visible}
      centered
      closable={false}
      maskClosable={false}
      width={isMobile ? "100%" : "75%"}
      className="dispatchStepsModal"
      bodyStyle={{ minHeight: 400 }}
      footer={renderFooterButtons}
      style={{
        minWidth: 680,
        maxWidth: 940,
        overflowX: "auto",
      }}
    >
      {renderBodyForCurrentStep}
    </Modal>
  );
};

export default connect(({ configs }) => ({
  configPerm: configs.configPerm,
}))(DispatchStepsModal);
